import React, { useContext } from "react";
import Styles from "./LogisticsServices.module.scss";
import {
    uselsAchievement,
    useLogisticsFeatures,
    useOtbHowItworkData,
    useCustomerSaysData
} from "../service-data/ServiceData";
import { useTranslation } from "next-i18next";
import { FaqsResponse } from "@vahak/core/dist/_services/faq.service";
import { ga4sendEvent } from "@vahak/core/dist/_services/user-analytics.service";

import { AppContext } from "@vahak/core/dist/app-context";

import Features from "../common/features/Features";
import Achievements from "../common/achievements/Achievements";
import HeroSection from "../common/hero-section/HeroSection";
import HowItWorks from "../common/how-it-works/HowItWorks";
import Faq from "../common/faq/Faq";

import { GA4EventNames, GA4ServicePageLogisticsServicesEventValues } from "@vahak/core/dist/constants/event-names";
import { getPlatformStatI18nLabels } from "@vahak/core/dist/constants/platformStats";
import { SOURCE_UTM } from "@vahak/core/dist/constants/misc";
import { PageTypes } from "@vahak/core/dist/constants/Pages";
import { ROUTES } from "@vahak/core/dist/constants/routeStrings";
import OurCustomerSays from "../common/our-customer-says/OurCustomerSays";
import Intro from "../common/intro/Intro";
import PageMetaTags from "../../common/page-meta-tags/PageMetaTags";
import { breadcrumbsConstants } from "constants/breadcrumbs-constants";
import ServiceInformation from "../common/service-information/ServiceInformation";

interface LogisticsServicesProps {
    faqs: FaqsResponse[];
}
const metaData = {
    title: "Logistics Service Provider, Top Logistic Company in India | Vahak",
    description:
        "Using India's largest network of logistics services provider companies, you can book trucks online & transport your goods anywhere in India today. Get a quote today!",
    keywords:
        "logistics services, logistics companies near me, logistics services provider, logistics company list, logistics services in mumbai, logistics services in india, logistics services in delhi, logistics transportation services, logistics services in chennai, companies looking for logistics services, logistic service provider company, transportation and logistics services, vehicle logistics services, logistics service provider in india, logistics services in indore, best logistics services in india, logistics services in ahmedabad, logistics services in kolkata, logistics services in pune, online logistics company in india, ",
    canonicalUrl: "https://www.vahak.in/logistics-services",
    schemaOrgJsonUrl: "/logistics-services",
    ogImgSrc: "https://d3ib4ot6oar1ep.cloudfront.net/1080/75/webp/transporters_with_loads.png",
    ogImgAltText: "Logistics Services",
    breadcrumbList: breadcrumbsConstants({}).logisticsServices
};

const LogisticsServices = ({ faqs }: LogisticsServicesProps) => {
    const { companyId, name, serviceType } = useContext(AppContext);
    const { t } = useTranslation("servicePages");

    return (
        <>
            <PageMetaTags
                title={metaData.title}
                description={metaData.description}
                keywords={metaData.keywords}
                canonicalUrl={metaData.canonicalUrl}
                ogUrl={metaData.canonicalUrl}
                ogImgSrc={metaData.ogImgSrc}
                ogImgAltText={metaData.ogImgAltText}
                schemaOrgJsonUrl={metaData.schemaOrgJsonUrl}
                breadcrumbList={metaData.breadcrumbList}
            />

            <main className={Styles.main}>
                <HeroSection
                    bannerTitle={t("servicePages:logistics-services.hero-section.bannerTitle")}
                    bannerText={t("servicePages:logistics-services.hero-section.bannerText")}
                    hrefLabel={t("servicePages:logistics-services.hero-section.hrefLabel")}
                    hrefURL={`${ROUTES.marketplace.getUrl("lorry")}?${SOURCE_UTM}=${PageTypes.LOGISTICS_SERVICE}`}
                    onLinkClick={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_logistics_services, {
                            value: GA4ServicePageLogisticsServicesEventValues.avail_logistics_sercives
                        });
                        ga4sendEvent(GA4EventNames.Avail_logistic_service_clicked_web, {
                            company_id: companyId,
                            user_name: name,
                            service_type: serviceType
                        });
                    }}
                    // srcSetURL={`${IMG_BASE_URL}/transporters_with_loads_3x.png 2052w, ${IMG_BASE_URL}/transporters_with_loads_2x.png 1368w, ${IMG_BASE_URL}/transporters_with_loads.png 684w, ${IMG_BASE_URL}/transporters_with_loads_mobile_3x.png 804w, ${IMG_BASE_URL}/transporters_with_loads_mobile_2x.png 536w, ${IMG_BASE_URL}/transporters_with_loads_mobile.png 268w`}
                    srcURL={`transporters_with_loads.png`}
                    altText="two transporters with loads and a truck"
                    sectionClass={Styles.herroSection}
                />
                <Achievements
                    achievementData={uselsAchievement()}
                    sectionTitle={t("servicePages:logistics-services.achievements.sectionTitle")}
                    sectionText={t(
                        "servicePages:logistics-services.achievements.sectionText",
                        getPlatformStatI18nLabels()
                    )}
                />
                <Intro text={t("servicePages:logistics-services.intro.text", getPlatformStatI18nLabels())} />
                <ServiceInformation
                    heading={t("servicePages:logistics-services.top-logistics-service-providers-in-india.heading")}
                    sectionTitle={""}
                    sectionText={t(
                        "servicePages:logistics-services.top-logistics-service-providers-in-india.description"
                    )}
                />
                <Features
                    sectionClass={Styles.featureSection}
                    featureCardClass={Styles.featureCard}
                    features={useLogisticsFeatures()}
                    sectionTitle={t("servicePages:logistics-services.features.sectionTitle")}
                    ctaLink={`${ROUTES.marketplace.getUrl("lorry")}`}
                    ctaLabel={t("servicePages:logistics-services.features.ctaLabel")}
                    onCtaClick={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_logistics_services, {
                            value: GA4ServicePageLogisticsServicesEventValues.find_trucks_online
                        });
                    }}
                />
                <HowItWorks
                    howitwork={useOtbHowItworkData()}
                    sectionTitle={t("servicePages:logistics-services.HowItWorks.sectionTitle")}
                    sectionText={t("servicePages:logistics-services.HowItWorks.sectionText")}
                    tabAppNav={t("servicePages:online-load-booking.HowItWorks.tabAppNav")}
                    tabWebNav={t("servicePages:online-load-booking.HowItWorks.tabWebNav")}
                    onAppTabChange={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_logistics_services, {
                            value: GA4ServicePageLogisticsServicesEventValues.mobile_app
                        });
                    }}
                    onWebTabChange={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_logistics_services, {
                            value: GA4ServicePageLogisticsServicesEventValues.web
                        });
                    }}
                />
                <Faq faqs={faqs} sectionTitle={t("servicePages:online-load-booking.Faq.sectionTitle")} />
                <OurCustomerSays
                    customerSaysData={useCustomerSaysData()}
                    sectionTitle={t("servicePages:logistics-services.ourCustomerSays.sectionTitle")}
                    sectionText={t("servicePages:logistics-services.ourCustomerSays.sectionText")}
                />
            </main>
        </>
    );
};

export default LogisticsServices;
